import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner, Card, Col, CardHeader, Row } from "reactstrap";
import LineChart from "components/shared/Charts/LineChart";
import { useQuery } from "@tanstack/react-query";
import { fetchVisitCards } from "store/actions/statsActions";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const generateCategories = (type, dateRange) => {
  if (type === "day") {
    const categories = [];
    for (let i = 0; i < 24; i += 2) {
      const start = dayjs().hour(i).minute(0).format("hA");
      const end = dayjs()
        .hour(i + 2)
        .minute(0)
        .format("hA");
      categories.push(`${start} - ${end}`);
    }
    return categories;
  } else if (type === "month") {
    const today = dayjs();
    const totalDays = today.daysInMonth();
    return Array.from({ length: totalDays }, (_, i) => i + 1);
  } else if (type === "year") {
    return [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
  } else if (type === "range") {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return [];
    let start = dayjs(startDate, "DD-MM-YYYY");
    const end = dayjs(endDate, "DD-MM-YYYY");
    const categories = [];
    while (start.isBefore(end) || start.isSame(end)) {
      categories.push(start.format("DD-MM-YYYY"));
      start = start.add(1, "day");
    }
    return categories;
  }
  return [];
};

const VisitCard = ({ type, dates, merchant }) => {
  const dispatch = useDispatch();
  const { startDate, endDate } = dates;
  const { user } = useSelector((state) => state.auth);
  const { latestVisits } = useSelector((state) => state.stats);

  const payload = {
    type,
    dateRange: { startDate, endDate },
    id:
      user?.type === "restaurant"
        ? user?.restaurantID
        : user?.type === "admin" && merchant?.id
        ? merchant.id
        : "",
  };

  const { isFetching, isError, error } = useQuery({
    queryKey: ["visitStats", payload],
    queryFn: () => dispatch(fetchVisitCards(payload)),
    staleTime: 60 * 60 * 1000,
    cacheTime: 70 * 60 * 1000,
    keepPreviousData: true,
  });

  if (isError) {
    toast.error(error?.message || "Failed to load visits data.");
  }

  const xAxisLabels = generateCategories(type, dates);

  const visitOptions = {
    chart: {
      id: "visit-line-chart",
      toolbar: { show: false },
    },
    xaxis: {
      categories: xAxisLabels,
      labels: {
        rotate: -45,
        style: { fontFamily: "Poppins, sans-serif", fontSize: "12px" },
      },
    },
    stroke: { curve: "smooth" },
    legend: { show: false },
    colors: ["#11cdef"],
  };

  const visitSeries = [
    { name: "Visits", data: Object.values(latestVisits || {}) },
  ];

  return (
    <>
      {user?.type === "admin" ? (
        <Row>
          <Col md={12}>
            <Card className="header-cards card overflow-hidden">
              <CardHeader className="d-flex justify-content-between flex-column flex-sm-row">
                <h3 className="mb-0">Visits</h3>
                <span className="text-muted fs-12">By Date</span>{" "}
              </CardHeader>
              {isFetching ? (
                <Spinner size="sm" color="primary" className="mx-auto my-5" />
              ) : (
                <LineChart options={visitOptions} series={visitSeries} />
              )}
            </Card>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col md={12}>
            <Card className="header-cards card overflow-hidden">
              <CardHeader className="d-flex justify-content-between flex-column flex-sm-row">
                <h3 className="mb-0">Visits</h3>
              </CardHeader>
              {isFetching ? (
                <Spinner size="sm" color="primary" className="mx-auto my-5" />
              ) : (
                <LineChart options={visitOptions} series={visitSeries} />
              )}
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default VisitCard;
